import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { Milestone, Rock, User, COLLECTION_NAMES } from '../types';

export interface CalendarMilestone extends Milestone {
  rock?: Rock & {
    owner?: User;
  };
}

export interface UseCalendarMilestonesParams {
  startDate: Date;
  endDate: Date;
  selectedUserId?: string; // 'all' or specific userId (filters by rock owner)
}

/**
 * Hook for fetching milestones within a date range for calendar display
 * Joins with rocks and rock owners for display
 * Supports filtering by rock owner
 */
export function useCalendarMilestones({
  startDate,
  endDate,
  selectedUserId,
}: UseCalendarMilestonesParams) {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [rocks, setRocks] = useState<Rock[]>([]);
  const { eosUsers = [] } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Memoize timestamps for dependency comparison
  const startTimestamp = useMemo(
    () => (startDate instanceof Date ? startDate : new Date(startDate)).getTime(),
    [startDate]
  );
  const endTimestamp = useMemo(
    () => (endDate instanceof Date ? endDate : new Date(endDate)).getTime(),
    [endDate]
  );

  // Load rocks for joining with milestones
  useEffect(() => {
    const rocksQuery = query(collection(db, COLLECTION_NAMES.ROCKS));
    const unsubscribe = onSnapshot(
      rocksQuery,
      (snapshot) => {
        const rocksData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dueDate: data.dueDate?.toDate?.() || data.dueDate,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
          } as Rock;
        });
        setRocks(rocksData);
      },
      (error) => {
        console.error('Error loading rocks:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Load milestones with date range
  useEffect(() => {
    setLoading(true);

    // Convert dates to Date objects
    const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);
    const endDateObj = endDate instanceof Date ? endDate : new Date(endDate);
    
    // Use timestamps for dependency comparison to prevent infinite loops
    const startTimestamp = startDateObj.getTime();
    const endTimestamp = endDateObj.getTime();

    const milestonesQuery = query(
      collection(db, COLLECTION_NAMES.MILESTONES),
      where('dueDate', '>=', startDateObj),
      where('dueDate', '<=', endDateObj),
      orderBy('dueDate', 'asc')
    );

    const unsubscribe = onSnapshot(
      milestonesQuery,
      (snapshot) => {
        const milestonesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dueDate: data.dueDate?.toDate?.() || data.dueDate,
            createdAt: data.createdAt || Timestamp.now(),
            completedAt: data.completedAt?.toDate?.() || data.completedAt,
          } as Milestone;
        });
        setMilestones(milestonesData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading milestones:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [startTimestamp, endTimestamp]);

  // Join milestones with rocks and filter by user if needed
  const milestonesWithJoins = useMemo(() => {
    const rocksMap = new Map(rocks.map((rock) => [rock.id, rock]));
    const usersMap = new Map(eosUsers.map((user) => [user.id, user]));

    let joinedMilestones: CalendarMilestone[] = milestones.map((milestone) => {
      const rock = milestone.rockId ? rocksMap.get(milestone.rockId) : undefined;
      const rockWithOwner = rock
        ? {
            ...rock,
            owner: rock.ownerId ? usersMap.get(rock.ownerId) : undefined,
          }
        : undefined;

      return {
        ...milestone,
        rock: rockWithOwner,
      } as CalendarMilestone;
    });

    // Filter by rock owner if selectedUserId is set
    if (selectedUserId && selectedUserId !== 'all') {
      joinedMilestones = joinedMilestones.filter(
        (milestone) => milestone.rock?.ownerId === selectedUserId
      );
    }

    return joinedMilestones;
  }, [milestones, rocks, eosUsers, selectedUserId]);

  return {
    milestones: milestonesWithJoins,
    loading,
  };
}

